import { useCallback, useEffect } from 'react';

import { Location } from 'history';
import { GlobalDocumentIds } from '../types/GlobalDocumentIds';

const THREE_HOURS = 10800000;
const SCROLL_POSITION_PREFIX = 'scrollPosition-';

type ScrollPosition = {
  /** window.scrollY */
  top: number;
  /** millis since epoch */
  timestamp: number;
};

export const getScrollPosition = (loc: Location): ScrollPosition | undefined => {
  const key = `${SCROLL_POSITION_PREFIX}${loc.key}`;
  const scrollPosition = sessionStorage.getItem(key);
  if (scrollPosition) {
    const { top, timestamp } = JSON.parse(scrollPosition) as ScrollPosition;
    const millisSinceEpoch = Date.now();
    if (millisSinceEpoch - timestamp > THREE_HOURS) {
      sessionStorage.removeItem(key);
      return;
    }
    return { top, timestamp };
  }
};

export function useScrollPosition(location: Location, storage: Storage = sessionStorage) {
  /**
   * Saves the scroll position for a given Location.
   * @param loc Location for which to save the current scroll position
   */
  const saveScrollPosition = useCallback(
    (loc: Location) => {
      const key = `${SCROLL_POSITION_PREFIX}${loc.key}`;
      const contentElement = document.getElementById(GlobalDocumentIds.scrollContent);
      const scrollPosition = { top: contentElement?.scrollTop, timestamp: Date.now() };
      storage.setItem(key, JSON.stringify(scrollPosition));
    },
    [storage],
  );

  const saveHandler = () => {
    saveScrollPosition(location);
  };

  // On mount we save last scroll position in storage
  useEffect(() => {
    window.addEventListener('beforeunload', saveHandler);

    return () => window.removeEventListener('beforeunload', saveHandler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { saveScrollPosition };
}
